import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Button, 
  Chip,
  Paper,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useState } from 'react';

// Services
import { firebaseWinesService } from '../services/firebaseService';
import { formatPrice } from '../utils/format';
import { useCartStore } from '../store/useStore';

const WineDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCartStore();
  const [quantity, setQuantity] = useState(1);

  // Fetch wine details
  const { data: wine, isLoading, error } = useQuery(
    ['wine', id],
    async () => {
      const response = await firebaseWinesService.getById(id);
      // El servicio de Firebase retorna { data: { data: { ... } } }
      return response.data.data;
    },
    {
      enabled: !!id
    }
  );

  const handleAddToCart = () => {
    if (wine) {
      addItem(wine, quantity);
    }
  };

  const handleQuantityChange = (change) => {
    setQuantity(prev => Math.max(1, prev + change));
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h6" textAlign="center">
          Cargando información del vino...
        </Typography>
      </Container>
    );
  }

  if (error || !wine) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h6" textAlign="center" color="error">
          Vino no encontrado
        </Typography>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button variant="contained" onClick={() => navigate('/tienda')}>
            Volver a la Tienda
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button 
        onClick={() => navigate('/tienda')} 
        sx={{ mb: 3 }}
      >
        ← Volver a la Tienda
      </Button>

      <Grid container spacing={4}>
        {/* Wine Image */}
        <Grid item xs={12} md={6}>
          <Box
            component="img"
            src={wine.imageUrl || wine.image || ''}
            alt={wine.name}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '500px',
              objectFit: 'contain',
              bgcolor: 'grey.100',
              borderRadius: 2
            }}
          />
        </Grid>

        {/* Wine Information */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Chip
              label={wine.denomination}
              color="primary"
              variant="outlined"
              sx={{ mb: 2 }}
            />
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 600,
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            {wine.name}
          </Typography>

          {wine.rating > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                bgcolor: 'secondary.main', 
                color: 'white', 
                px: 2, 
                py: 1, 
                borderRadius: 1,
                fontSize: '1.2rem',
                fontWeight: 600
              }}>
                {wine.rating}
              </Box>
              <Typography variant="h6" sx={{ ml: 2, color: 'text.secondary' }}>
                {wine.ratingSource}
              </Typography>
            </Box>
          )}

          <Typography
            variant="h4"
            color="primary"
            sx={{ fontWeight: 700, mb: 3 }}
          >
            {formatPrice(wine.price)}
          </Typography>

          <Typography
            variant="body1"
            sx={{ mb: 3, lineHeight: 1.6 }}
          >
            {wine.description}
          </Typography>

          {/* Wine Details */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Detalles del Vino
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Tipo:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {wine.type}
                </Typography>
              </Grid>
              {wine.year && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Añada:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {wine.year}
                  </Typography>
                </Grid>
              )}
              {wine.alcoholContent && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Graduación:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {wine.alcoholContent}%
                  </Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Stock:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {wine.stock > 0 ? `${wine.stock} unidades` : 'Sin stock'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Quantity and Add to Cart */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="h6">Cantidad:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
              >
                <RemoveIcon />
              </IconButton>
              <Typography 
                variant="h6" 
                sx={{ 
                  minWidth: '40px', 
                  textAlign: 'center',
                  px: 2,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1
                }}
              >
                {quantity}
              </Typography>
              <IconButton 
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= wine.stock}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Box>

          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={handleAddToCart}
            disabled={wine.stock === 0}
            sx={{
              width: '100%',
              bgcolor: 'secondary.main',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'secondary.dark'
              }
            }}
          >
            {wine.stock === 0 ? 'Sin Stock' : 'Añadir al Carrito'}
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default WineDetail; 